import React, { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, Outlet } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { AppLayout } from "./layouts/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { useAccountType } from "../hooks/useAccountType";
import brandConfig from "../config/brandConfig";

/**
 * Language-neutral suspense fallback.
 * This boundary renders before the page (and its translations) are loaded, so a
 * spinner is used instead of a hardcoded English "Loading..." string.
 */
const RouteFallback = () => (
  <Box
    sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}
    role="progressbar"
  >
    <CircularProgress />
  </Box>
);

const DashboardPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.Dashboard,
  }))
);
const AnimalsPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.Animals,
  }))
);
const AnimalPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.Animal,
  }))
);
const ArchivePage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.Archive,
  }))
);
const BarnPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.Barn,
  }))
);
const BarnSectionPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.BarnSection,
  }))
);
const SettingsPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.Settings,
  }))
);
const TreatmentsPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.Treatments,
  }))
);
const WorkPlanerPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.WorkPlaner,
  }))
);
const LoginPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.Login,
  }))
);
const SignupPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.Signup,
  }))
);
const BiotDashboardPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.BiotDashboard,
  }))
);
const PlanerAIPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.PlanerAI,
  }))
);

const CalfERPPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.CalfERP,
  }))
);
const CalfDashboardPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.CalfDashboard,
  }))
);
const CockpitPage = lazy(() => import("../pages/cockpit/Cockpit"));


const DailyReportsPage = lazy(() =>
  import("../components/activity-tracking/TrackedPages").then((module) => ({
    default: module.DailyReports,
  }))
);

const AcceptInvitationPage = lazy(() =>
  import("../pages/accept-invitation/AcceptInvitation").then((module) => ({
    default: module.default,
  }))
);

interface RouteDefinition {
  path?: string;
  index?: boolean;
  title?: string;
  breadcrumb?: string;
  element: React.ReactElement;
}

const APP_NAME = brandConfig.name;

const RouteMetadata: React.FC<{ title?: string; breadcrumb?: string; children: React.ReactNode }> = ({
  title,
  breadcrumb,
  children,
}) => {
  useEffect(() => {
    if (title) {
      document.title = `${title} | ${APP_NAME}`;
    }
  }, [title]);

  useEffect(() => {
    if (breadcrumb) {
      document.body.dataset.breadcrumb = breadcrumb;
    } else {
      delete document.body.dataset.breadcrumb;
    }
  }, [breadcrumb]);

  return <>{children}</>;
};

/**
 * Route guard: redirects to dashboard if user doesn't have BioTag hardware.
 */
const RequireBioTag: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { hasBioTag } = useAccountType();
  if (!hasBioTag) return <Navigate to="/" replace />;
  return children;
};

/**
 * Route guard: redirects to dashboard if user doesn't have ERP/herd management.
 */
const RequireERP: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { hasERP } = useAccountType();
  if (!hasERP) return <Navigate to="/" replace />;
  return children;
};

const protectedRoutes: RouteDefinition[] = [
  {
    index: true,
    title: "Dashboard",
    breadcrumb: "Dashboard",
    element: <DashboardPage />,
  },
  {
    path: "animals",
    title: "Animals",
    breadcrumb: "Animals",
    element: <AnimalsPage />,
  },
  {
    path: "animal",
    title: "Animal",
    breadcrumb: "Animal",
    element: <AnimalPage />,
  },
  {
    path: "animal/:animalId",
    title: "Animal",
    breadcrumb: "Animal",
    element: <AnimalPage />,
  },
  {
    path: "archive",
    title: "Archive",
    breadcrumb: "Archive",
    element: <ArchivePage />,
  },
  {
    path: "barn",
    title: "Barn",
    breadcrumb: "Barn",
    element: <BarnPage />,
  },
  {
    path: "barn-section",
    title: "Barn Section",
    breadcrumb: "Barn Section",
    element: <BarnSectionPage />,
  },
  {
    path: "planner-ai",
    title: "Planer & AI",
    breadcrumb: "Planer & AI",
    element: <RequireBioTag><PlanerAIPage /></RequireBioTag>,
  },
  {
    path: "predictions",
    title: "Predictions",
    breadcrumb: "Predictions",
    element: <Navigate to="/planner-ai" replace />,
  },
  {
    path: "settings",
    title: "Settings",
    breadcrumb: "Settings",
    element: <SettingsPage />,
  },
  {
    path: "treatments",
    title: "Treatments",
    breadcrumb: "Treatments",
    element: <TreatmentsPage />,
  },
  {
    path: "workplaner",
    title: "Work Planner",
    breadcrumb: "Work Planner",
    element: <WorkPlanerPage />,
  },

  {
    path: "biotdashboard",
    title: "BioT Dashboard",
    breadcrumb: "BioT Dashboard",
    element: <RequireBioTag><BiotDashboardPage /></RequireBioTag>,
  },

  {
    path: "cockpit",
    title: "Cockpit",
    breadcrumb: "Cockpit",
    element: <CockpitPage />,
  },
  {
    path: "calf-erp",
    title: "Calf ERP",
    breadcrumb: "Calf ERP",
    element: <CalfERPPage />,
  },
  {
    path: "calf-dashboard",
    title: "Calf Dashboard",
    breadcrumb: "Calf Dashboard",
    element: <CalfDashboardPage />,
  },

  {
    path: "daily-reports",
    title: "Daily Reports",
    breadcrumb: "Daily Reports",
    element: <RequireERP><DailyReportsPage /></RequireERP>,
  },
];

const ProtectedRoute: React.FC = () => {
  const { currentUser } = useAuth();

  if (!currentUser || currentUser.emailVerified === false) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

export const AppRoutes: React.FC = () => (
  <Routes>
    <Route
      path="/login"
      element={
        <RouteMetadata title="Login" breadcrumb="Login">
          <LoginPage />
        </RouteMetadata>
      }
    />

    <Route
      path="/signup"
      element={
        <RouteMetadata title="Sign Up" breadcrumb="Sign Up">
          <SignupPage />
        </RouteMetadata>
      }
    />

    <Route
      path="/accept-invitation"
      element={
        <RouteMetadata title="Accept Invitation" breadcrumb="Accept Invitation">
          <Suspense fallback={<RouteFallback />}>
            <AcceptInvitationPage />
          </Suspense>
        </RouteMetadata>
      }
    />

    <Route element={<ProtectedRoute />}>
      {/* Main App Layout */}
      <Route element={<AppLayout />}>
        {protectedRoutes.map((route) => (
          <Route
            key={route.path ?? "index"}
            index={route.index}
            path={route.path}
            element={
              <RouteMetadata title={route.title} breadcrumb={route.breadcrumb}>
                {route.element}
              </RouteMetadata>
            }
          />
        ))}
      </Route>
    </Route>

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

export default AppRoutes;
